import { useEffect, useState } from "react";
import { BadgeCheck, ExternalLink, Star } from "lucide-react";
import { api } from "../../lib/api";
import { Reveal } from "./Reveal";

const num = (v) => (typeof v === "number" ? v : null);

function RatingCard({ source, rating, count, countLabel, url, cta, note, tone, testid, delay }) {
  return (
    <Reveal delay={delay} className={`flex flex-col rounded-[2rem] p-8 ${tone}`}>
      <p className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.16em]">{source}</p>
      {rating !== null && (
        <div className="mt-5 flex items-end gap-4" data-testid={`${testid}-rating`}>
          <span className="font-display text-7xl font-extrabold leading-none">{rating.toLocaleString("fr-FR")}</span>
          <div className="pb-2">
            <div className="flex gap-0.5 text-ag-yellow">{[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-5 w-5 fill-current" />)}</div>
            <p className="mt-1 text-sm opacity-80">sur 5{count !== null && ` · ${count} ${countLabel}`}</p>
          </div>
        </div>
      )}
      {note && <p className="mt-4 text-sm opacity-80">{note}</p>}
      <div className="flex-1" />
      <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-yellow mt-7 w-full justify-center" data-testid={`${testid}-button`}>{cta} <ExternalLink className="h-4 w-4" /></a>
    </Reveal>
  );
}

export function ReviewsSection() {
  const [s, setS] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/settings").then((r) => { if (alive) setS(r.data || null); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const googleUrl = s?.google_reviews_url || "https://www.google.com/maps/search/?api=1&query=A%2BGLASS+Pare-Brise+HAGUENAU";
  const alloUrl = s?.allogarage_url || "https://www.allogarage.fr/garages/details-garage-A-GLASS-HAGUENAU-51080.html";
  return (
    <section className="px-5 py-24 lg:px-8" data-testid="reviews-section">
      <div className="mx-auto grid max-w-[1320px] gap-10 lg:grid-cols-12 lg:items-end">
        <Reveal className="lg:col-span-5">
          <p className="eyebrow">Avis clients</p>
          <h2 className="mt-3 font-display text-4xl font-extrabold leading-[1] tracking-tight text-ag-ink sm:text-5xl lg:text-6xl">Ils nous font confiance</h2>
          <p className="mt-5 max-w-lg text-slate-600">Les avis de nos clients sont publiés sur notre fiche Google et sur Allogarage, où ils sont vérifiés. Consultez-les en toute transparence.</p>
          <a href={alloUrl} target="_blank" rel="noopener noreferrer" className="group mt-8 flex max-w-sm items-center gap-5 rounded-[1.5rem] bg-white p-4 ring-1 ring-slate-200 transition-shadow hover:shadow-lg" data-testid="reviews-best-garage-badge">
            <img src="/images/badge-meilleur-garage-2026.webp" alt="Badge Allogarage Sélection Meilleurs Garages de France 2026" loading="lazy" width="500" height="500" className="h-24 w-24 shrink-0 rounded-xl transition-transform duration-500 group-hover:scale-105" />
            <span>
              <span className="block font-display text-lg font-extrabold leading-tight text-ag-ink">Sélection Meilleurs Garages de France 2026</span>
              <span className="mt-1 block text-sm text-slate-600">Distinction Allogarage basée sur les avis clients vérifiés</span>
            </span>
          </a>
        </Reveal>
        <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
          <RatingCard source="Google" rating={num(s?.google_rating)} count={num(s?.google_review_count)} countLabel="avis Google" url={googleUrl} cta="Voir tous nos avis Google" tone="bg-ag-ink text-white" testid="reviews-google" delay={0.08} />
          <RatingCard source={<><BadgeCheck className="h-5 w-5" />Allogarage · avis vérifiés</>} rating={num(s?.allogarage_rating)} count={num(s?.allogarage_review_count)} countLabel="avis vérifiés" url={alloUrl} cta="Voir nos avis vérifiés" note="Avis vérifiés norme AFNOR NF 20488 sur Allogarage." tone="bg-ag-navy text-white" testid="reviews-allogarage" delay={0.16} />
        </div>
      </div>
    </section>
  );
}
